<template>
  <v-card outlined class="dd-stripe-card">
    <div class="dd-stripe" :class="stripeColor"></div>
    <div class="px-4 py-3">
      <div class="d-flex align-center flex-wrap" style="gap: 8px;">
        <span class="text-subtitle-1 font-weight-medium">{{ queueItem.tractName }}</span>
        <v-chip v-if="isApproved" small color="success" text-color="white" label>
          <v-icon small left>mdi-check-decagram</v-icon>{{ $t('approved') }}
        </v-chip>
        <v-chip v-else-if="isRejected" small color="error" text-color="white" label>
          <v-icon small left>mdi-close-octagon</v-icon>{{ $t('rejected') }}
        </v-chip>
        <v-chip v-if="isOwnSubmission && !isDecided" small outlined label>
          <v-icon small left>mdi-account-lock-outline</v-icon>{{ $t('yourSubmission') }}
        </v-chip>
        <v-chip v-if="exemption" small outlined color="primary" label>
          <v-icon small left>mdi-shield-check-outline</v-icon>
          {{ $t('exemption') }} · {{ exemption.name }}
        </v-chip>
        <span v-if="isDecided && queueItem.resolvedByUserName" class="text-caption grey--text text--darken-1 d-inline-flex align-center">
          <v-icon x-small class="mr-1">mdi-account-check-outline</v-icon>
          {{ $t('reviewed') }} {{ $t('by') }} {{ queueItem.resolvedByUserName }}
        </span>
        <span v-if="isDecided && queueItem.resolvedAt" class="text-caption grey--text text--darken-1 d-inline-flex align-center">
          <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>{{ resolvedDate }}
        </span>
        <v-spacer/>
        <v-btn small color="success" class="tertiary--text" @click="$emit('view')">
          <v-icon small left>mdi-clipboard-text-search-outline</v-icon>
          {{ $t('view') }}
        </v-btn>
      </div>

      <div class="text-caption grey--text text--darken-1 mt-1 d-flex align-center flex-wrap" style="gap: 4px 16px;">
        <span v-if="locationAndAcres">
          <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ locationAndAcres }}
        </span>
        <span v-if="harvestSummary">
          <v-icon x-small class="mr-1">mdi-saw-blade</v-icon>{{ harvestSummary }}
        </span>
        <span v-if="queueItem.requestedAt">
          <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
          {{ $t('submitted') }} {{ submittedDate }} · {{ submittedAge }}
        </span>
        <span v-if="foresterName">
          <v-icon x-small class="mr-1">mdi-account-hard-hat-outline</v-icon>
          {{ $t('by') }} {{ foresterName }}
        </span>
      </div>

      <div v-if="isDecided && queueItem.comment" class="text-caption font-italic grey--text text--darken-1 mt-1 d-flex align-start">
        <v-icon x-small class="mr-1 mt-1">mdi-comment-quote-outline</v-icon>
        <span class="ddq-comment-clamp">{{ queueItem.comment }}</span>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <span class="dd-eyebrow mr-1">{{ $t('groupHits') }}</span>
        <v-chip
        v-for="hit in groupHits"
        :key="hit.key"
        small
        outlined>
          <v-icon small left :color="hit.color">mdi-square-rounded</v-icon>
          {{ hit.label }}
        </v-chip>
        <v-chip v-if="groupHits.length === 0" small outlined>
          {{ $t('none') }}
        </v-chip>
        <v-spacer/>
        <div v-if="hasConversionField" class="d-flex align-center ddq-conversion-slot" :style="conversionSlotStyle">
          <span class="dd-eyebrow">{{ $t('conversion') }}</span>
          <ConversionImpactChip :impact="conversionImpact" :text="conversionName"/>
        </div>
        <div class="d-flex align-center justify-end ddq-habitat-slot" style="gap: 8px;">
          <span class="dd-eyebrow">{{ $t('habitat') }}</span>
          <PresenceChip :present="anyPresence"/>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { utcToLocalDate, daysAgoInCompanyOffset } from '@/utils/DateFormatter.js'
import { isApproved, isRejected, isDecided, locationAndAcres, harvestSummary, hasConversionField } from '@/utils/dueDiligence/presentation.js'
import { compareGroupRowPrecedence } from '@/utils/dueDiligence/groupPrecedence.js'

export default {
  name: 'ApprovalQueueCard',

  components: {
    PresenceChip: () => import('@/components/due-diligence/PresenceChip.vue'),
    ConversionImpactChip: () => import('@/components/due-diligence/ConversionImpactChip.vue')
  },

  props: {
    queueItem: {
      type: Object,
      required: true
    },
    conversionSlotWidth: {
      type: Number,
      default: null
    }
  },

  computed: {
    ...mapGetters('user', ['userInfo']),

    exemption () {
      return this.queueItem.dueDiligenceExemption ?? null
    },

    isOwnSubmission () {
      return this.queueItem.requestedByUserId === this.userInfo.applicationUserId
    },

    isApproved () {
      return isApproved(this.queueItem)
    },

    isRejected () {
      return isRejected(this.queueItem)
    },

    isDecided () {
      return isDecided(this.queueItem)
    },

    resolvedDate () {
      return this.queueItem.resolvedAt ? utcToLocalDate(this.queueItem.resolvedAt) : ''
    },

    stripeColor () {
      if (this.isApproved) return 'success'
      if (this.isRejected) return 'error'
      return 'primary'
    },

    locationAndAcres () {
      return locationAndAcres(this.queueItem)
    },

    harvestSummary () {
      return harvestSummary(this.queueItem)
    },

    hasConversionField () {
      return hasConversionField(this.queueItem)
    },

    conversionImpact () {
      return this.queueItem.conversion?.impact ?? null
    },

    conversionName () {
      return this.queueItem.conversion?.name ?? null
    },

    conversionSlotStyle () {
      return {
        gap: '8px',
        flexBasis: this.conversionSlotWidth ? `${this.conversionSlotWidth}px` : 'auto'
      }
    },

    foresterName () {
      return this.queueItem.requestedByUserName
    },

    submittedDate () {
      return this.queueItem.requestedAt ? utcToLocalDate(this.queueItem.requestedAt) : ''
    },

    submittedAge () {
      const days = daysAgoInCompanyOffset(this.queueItem.requestedAt)
      if (days <= 0) return this.$t('today')
      if (days === 1) return this.$t('yesterday')
      return this.$t('nDaysAgo', { n: days })
    },

    groupHits () {
      return [...(this.queueItem.groupSpeciesCounts ?? [])].sort(compareGroupRowPrecedence).map(hit => ({
        key: hit.mitigationGroupId,
        color: hit.color ?? 'primary',
        label: hit.speciesCount > 0
          ? `${hit.mitigationGroupName} · ${this.$t('nSpecies', { n: hit.speciesCount })}`
          : hit.mitigationGroupName
      }))
    },

    anyPresence () {
      return this.queueItem.anyRiskPresence === true
    }
  }
}
</script>

<style scoped>
/* A reviewer comment has no length limit — clamp it so one long note can't stretch the card */
.ddq-comment-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ddq-conversion-slot {
  flex-grow: 0;
  flex-shrink: 0;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  margin-right: 16px;
}
.ddq-habitat-slot {
  min-width: 180px;
}
</style>
